import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../utils/authService';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import {
  Button, Card, EmptyState, Note, SkeletonPage, Sym, useCompany, usePageTitle,
} from '../../components/app2';

import SyncStatusCard from './components/SyncStatusCard';
import PeriodPicker from './components/PeriodPicker';

/**
 * Descargar recibos del SAT.
 *
 * ── El cambio de jul-2026 ───────────────────────────────────────────────────
 * Esta pantalla YA NO conduce la sincronización: la encola y observa. Antes
 * creaba la solicitud y se quedaba verificando cada 10 s durante 10 minutos
 * como máximo; si cerrabas la pestaña, la solicitud quedaba viva en el SAT sin
 * que nadie la cosechara, y con una cola que va de segundos a 26 horas esos 10
 * minutos casi nunca alcanzaban.
 *
 * Ahora el orquestador del servidor la lleva de punta a punta. Aquí solo se
 * pregunta cada 5 s cómo va, y solo mientras hay algo vivo. Cerrar la pantalla
 * dejó de tener consecuencias.
 */

const REFRESCO_MS = 5000;

const PayrollProcessing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [profileData, setProfileData] = useState(null);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  const [encolando, setEncolando] = useState(false);
  /** Lo que el SERVIDOR dice de la sincronización de esta empresa. */
  const [solicitud, setSolicitud] = useState(null);

  const temporizador = useRef(null);

  const { idEmpresaActiva, setSincronizacionEnCurso } = useCompany();
  const navigationState = location?.state;
  const targetCompanyId = navigationState?.companyId || idEmpresaActiva;

  const nombreEmpresa = currentCompany?.company_name
    || currentCompany?.businessName
    || currentCompany?.name
    || navigationState?.companyName
    || 'Empresa';

  usePageTitle({ title: currentCompany ? nombreEmpresa : null });

  // ── Empresa activa ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelado = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        setSolicitud(null);

        const profile = await authService?.getUserProfile();
        let elegida = null;

        if (profile?.companies?.length) {
          if (targetCompanyId) {
            // Comparación laxa: el id llega como número (contexto) o texto (URL).
            elegida = profile.companies.find((c) => (
              Number(c?.id) === Number(targetCompanyId)
              || Number(c?.company_id) === Number(targetCompanyId)
            ));
          }
          if (!elegida) elegida = profile.companies[0];
          if (cancelado) return;
          setProfileData(profile);
          setCurrentCompany(elegida);
        } else {
          const userData = authService?.getCurrentUser();
          if (userData?.companies?.length > 0 && !cancelado) setCurrentCompany(userData.companies[0]);
        }
      } catch (e) {
        console.error('Error cargando el perfil:', e);
        if (!cancelado) setError('No se pudieron cargar los datos de la empresa.');
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => { cancelado = true; };
  }, [targetCompanyId]);

  // ── Observación del servidor ───────────────────────────────────────────────
  const refrescarEstadoServidor = useCallback(async () => {
    if (!currentCompany?.id) return;
    try {
      const vivas = await strapiService.satSync.enCurso(currentCompany.id);
      const viva = Array.isArray(vivas?.data) ? vivas.data[0] : null;

      if (viva) {
        setSolicitud(viva);
        setDateRange({
          startDate: String(viva.date_from || '').slice(0, 10),
          endDate: String(viva.date_to || '').slice(0, 10),
        });
        return;
      }

      // Nada vivo: se muestra la última terminada, que es lo que el usuario
      // quiere ver al volver ("¿en qué acabó lo que dejé corriendo?").
      const ultimas = await strapiService.satSync.historico({
        companyId: currentCompany.id, limit: 1,
      });
      setSolicitud(Array.isArray(ultimas?.data) ? ultimas.data[0] || null : null);
    } catch (e) {
      console.error('Error consultando el estado de la sincronización:', e);
    }
  }, [currentCompany?.id]);

  useEffect(() => { refrescarEstadoServidor(); }, [refrescarEstadoServidor]);

  /** Solo se insiste mientras hay algo vivo: si terminó, no se golpea al servidor. */
  const vivo = solicitud && ['encolada', 'solicitud_aceptada', 'en_proceso', 'lista_para_descargar', 'descargando']
    .includes(solicitud.status);

  useEffect(() => {
    clearInterval(temporizador.current);
    if (!vivo) return undefined;
    temporizador.current = setInterval(refrescarEstadoServidor, REFRESCO_MS);
    return () => clearInterval(temporizador.current);
  }, [vivo, refrescarEstadoServidor]);

  // Aviso al cambiar de empresa mientras algo corre.
  useEffect(() => {
    if (vivo && currentCompany?.id) {
      setSincronizacionEnCurso({ idEmpresa: currentCompany.id, nombre: nombreEmpresa });
    } else {
      setSincronizacionEnCurso(null);
    }
    return () => setSincronizacionEnCurso(null);
  }, [vivo, currentCompany?.id, nombreEmpresa, setSincronizacionEnCurso]);

  // ── Acción: encolar ────────────────────────────────────────────────────────
  /**
   * Qué hacer cuando el servidor dice que este periodo ya está (total o
   * parcialmente) cubierto.
   *
   * No es un error: es la protección de la cuota. El SAT limita cuántas veces se
   * puede pedir un mismo periodo DE POR VIDA, así que aquí se pregunta en vez de
   * gastar un cupo irrecuperable en algo que no traería nada nuevo.
   *
   * Devuelve qué reintentar, o null si el usuario se echa para atrás.
   */
  const decidirSobreCobertura = async (respuesta) => {
    const cobertura = respuesta?.data?.cobertura;
    const mensaje = respuesta?.error || cobertura?.mensaje || '';

    if (respuesta?.code === 'PERIODO_EN_PROCESO') {
      AlertService.warning('Ya se está pidiendo', mensaje);
      return null;
    }

    if (respuesta?.code === 'PERIODO_PARCIAL' && cobertura?.sugerencia) {
      const soloElHueco = await AlertService.confirm(
        'Parte de ese periodo ya la tienes',
        mensaje,
        { confirmText: 'Pedir solo lo que falta', cancelText: 'Mejor no' },
      );
      return soloElHueco ? { aceptarSugerencia: true } : null;
    }

    if (respuesta?.code === 'PERIODO_PROVISIONAL') {
      // Ya se pidió, pero puede haber recibos timbrados después. No se bloquea:
      // se explica y se deja elegir, porque a veces uno tiene motivos para
      // sospechar que falta algo y no quiere esperar al barrido.
      const repetir = await AlertService.confirm(
        'Ya lo pediste, pero puede faltar algo',
        mensaje,
        {
          confirmText: 'Pedirlo otra vez',
          cancelText: 'Esperar al barrido',
          confirmColor: '#f59e0b',
          icon: 'info',
        },
      );
      return repetir ? { forzar: true } : null;
    }

    if (respuesta?.code === 'PERIODO_CUBIERTO') {
      // El escape consciente: por si un día se bajó incompleto y hace falta
      // insistir. Se dice sin rodeos que cuesta un cupo que no se recupera.
      const insistir = await AlertService.confirm(
        'Ese periodo ya está descargado',
        `${mensaje} ¿Quieres pedirlo de todas formas?`,
        {
          confirmText: 'Sí, pedirlo igual',
          cancelText: 'No, dejarlo así',
          confirmColor: '#f59e0b',
          icon: 'warning',
        },
      );
      return insistir ? { forzar: true } : null;
    }

    AlertService.warning('No se pudo encolar', mensaje || 'Ya hay una sincronización en curso para esta empresa.');
    return null;
  };

  const iniciar = async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) {
      AlertService.warning('Falta el periodo', 'Elige las fechas que quieres descargar del SAT.');
      return;
    }
    if (!currentCompany?.id) return;

    try {
      setEncolando(true);
      let r = await strapiService.satSync.crear({
        companyId: currentCompany.id,
        dateFrom: dateRange.startDate,
        dateTo: dateRange.endDate,
      });

      // El backend responde 409 con una decisión que solo el usuario puede tomar.
      const decidibles = ['PERIODO_CUBIERTO', 'PERIODO_PARCIAL', 'PERIODO_EN_PROCESO', 'PERIODO_PROVISIONAL'];
      if (r?.success === false && decidibles.includes(r.code)) {
        const decision = await decidirSobreCobertura(r);
        if (!decision) return;
        r = await strapiService.satSync.crear({
          companyId: currentCompany.id,
          dateFrom: dateRange.startDate,
          dateTo: dateRange.endDate,
          ...decision,
        });
      }

      if (r?.success === false) {
        AlertService.warning('No se pudo encolar', r.error || 'Ya hay una sincronización en curso para esta empresa.');
        return;
      }

      const pedido = r?.data?.date_from && r?.data?.date_to
        ? ` Se pidió del ${String(r.data.date_from).slice(0, 10)} al ${String(r.data.date_to).slice(0, 10)}.`
        : '';
      AlertService.success(
        'Sincronización encolada',
        `Corre en el servidor: puedes cerrar esta pantalla y volver cuando quieras.${pedido}`,
      );
      await refrescarEstadoServidor();
    } catch (e) {
      const mensaje = e?.response?.data?.error?.message
        || e?.response?.data?.error
        || e?.message
        || 'No se pudo encolar la sincronización';
      AlertService.error('No se pudo encolar', String(mensaje));
    } finally {
      setEncolando(false);
    }
  };

  if (loading) return <SkeletonPage kpis={0} rows={5} />;

  if (error && !currentCompany) {
    return (
      <EmptyState
        icon="error"
        size="lg"
        title="No se pudo cargar la sincronización"
        text={error}
        action={
          <Button variant="outline" icon="refresh" onClick={() => window.location?.reload()}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <div className="a2-anim-screen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      <Card pad={false} style={{ padding: 'clamp(20px,3vw,28px)' }}>
        <div
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end',
            justifyContent: 'space-between', marginBottom: 24,
          }}
        >
          <div style={{ maxWidth: 470 }}>
            <h3 className="a2-card-title" style={{ fontSize: '1.35rem' }}>Descargar recibos del SAT</h3>
            <p style={{ font: "400 .95rem/1.5 'Hanken Grotesk'", color: 'var(--muted)', margin: '6px 0 0' }}>
              Elige el periodo y PaySlip trae del SAT los recibos ya timbrados de{' '}
              <strong style={{ color: 'var(--heading)' }}>{nombreEmpresa}</strong>.{' '}
              El proceso corre en el servidor: puedes cerrar esta pantalla.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <PeriodPicker
              dateRange={dateRange}
              onChange={(next) => setDateRange(next)}
              disabled={Boolean(vivo) || encolando}
            />

            <Button
              icon="sync"
              onClick={iniciar}
              loading={encolando}
              disabled={!dateRange.startDate || !dateRange.endDate || Boolean(vivo)}
            >
              Iniciar sincronización
            </Button>
          </div>
        </div>

        <SyncStatusCard solicitud={solicitud} vivo={Boolean(vivo)} />

        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {solicitud ? (
            <Button variant="outline" size="sm" icon="visibility"
              onClick={() => navigate(`/app/sync/${solicitud.id}`)}
            >
              Ver el detalle y la bitácora
            </Button>
          ) : null}
          <Button variant="outline" size="sm" icon="history" onClick={() => navigate('/app/sync')}>
            Ver todas las sincronizaciones
          </Button>
          {solicitud?.status === 'terminada' ? (
            <Button variant="outline" size="sm" icon="receipt_long" onClick={() => navigate('/app/receipts')}>
              Ver los recibos
            </Button>
          ) : null}
        </div>
      </Card>

      {vivo ? (
        <Note tone="info" icon="dns">
          <span>
            <strong style={{ color: 'var(--heading)' }}>Puedes cerrar esta pantalla.</strong>{' '}
            El servidor sigue con la descarga aunque cierres el navegador; al volver verás en qué acabó.
          </span>
        </Note>
      ) : null}
    </div>
  );
};

export default PayrollProcessing;
